import { useFormik } from 'formik'
import { debounce } from 'lodash'
import { useEffect, useState } from 'react'
import ModalComponent from '..'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../../constants'
import { useBulkUploadProductsMutation } from '../../../redux-toolkits/products/products.slice'
import { useLazyGetAllVendorsQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import { BulkUploadProductsSchema } from '../../../validation/validate'
import { Button } from '../../button'
import Icon from '../../icon'
import Dropdown from '../../inputs/dropdown'
import { FileUploadInput } from '../../inputs/fileUpload'
import { TextInput } from '../../inputs/textInput'

const handleSearch = (searchFunction: (val?: any) => void, query?: any) => {
  searchFunction(query)
}

const handleDebouncedSearch = debounce(handleSearch, 800)

export const BulkUploadProductsModal = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccess = () => {},
}: {
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccess?: () => void
}) => {
  const [vendorSearch, setVendorSearch] = useState<string>()

  const [getAllVendors, { data: vendors, isFetching: fetchingVendors }] =
    useLazyGetAllVendorsQuery()

  const [doBulkUpload, { isLoading }] = useBulkUploadProductsMutation()

  const {
    touched,
    handleBlur,
    setFieldValue,
    errors,
    values,
    isValid,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      vendor: { _id: '', name: '' },
      file: [],
    },
    validationSchema: BulkUploadProductsSchema,
    onSubmit: async (vals) => {
      const formData = new FormData()

      formData.append('vendorId', vals.vendor._id)
      if ((vals.file as File[])[0].type.includes('csv')) {
        formData.append('itemsCsv', vals.file[0])
      } else {
        formData.append('itemsXlsx', vals.file[0])
      }

      const resp = await doBulkUpload(formData)

      if (resp.data?.success) {
        resetForm()
        setOpen(false)
        onCloseCallback()
        onSuccess()
      }
    },
  })

  useEffect(() => {
    handleDebouncedSearch(getAllVendors, {
      page: `1`,
      limit: NUMBER_OF_ITEMS_PER_PAGE.toString(),
      search: vendorSearch,
    })
  }, [vendorSearch])
  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={onCloseCallback}
      title={'Bulk Upload Products'}
      childrenClassName="md:!max-w-[420px]"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Dropdown
          loading={fetchingVendors}
          className=""
          menuClassName="!max-h-[200px]"
          menuButton={
            <TextInput
              type="text"
              name="vendor"
              placeholder="Creator"
              value={vendorSearch ? vendorSearch : values.vendor.name}
              onBlur={handleBlur}
              onChange={(e) => {
                setVendorSearch(e.target.value)
              }}
              errors={
                errors.vendor?._id && touched.vendor?._id
                  ? errors.vendor._id
                  : ''
              }
              hasIcon={false}
              readOnly={false}
              extra={
                <Icon
                  id={'left-caret'}
                  width={16}
                  height={16}
                  className="-rotate-90 text-sec-black"
                />
              }
              className="!border-divider-gray"
            />
          }
          menuItems={vendors?.data.vendors.map((vendor) => {
            return {
              name: vendor.businessName,
              value: vendor._id,
              onClick: () => {
                setFieldValue('vendor', {
                  _id: vendor._id,
                  name: vendor.businessName,
                })
                setVendorSearch(undefined)
              },
            }
          })}
        />

        <FileUploadInput
          maxNumberOfFiles={1}
          handleChange={(files) => {
            setFieldValue('file', Object.values(files))
          }}
          selectedFiles={
            (values.file as File[])?.length > 0 ? (values?.file as File[]) : []
          }
          setSelectedFiles={(data) => {
            setFieldValue('file', data)
          }}
          errors={errors?.file ? (errors?.file as string) : ''}
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          types="CSV, XLSX"
        />

        <Button
          type="submit"
          label="Upload"
          loading={isLoading}
          disabled={!isValid || isLoading}
          onClick={() => {}}
          className=""
          primary
        />
      </form>
    </ModalComponent>
  )
}
